import { useEffect, useState } from "react";
import apiClient, { mediaUrl, formatApiError } from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Image as ImageIcon, Upload, X, Search } from "lucide-react";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

/**
 * MediaPicker — opens a dialog to either upload a new image or pick one from
 * the existing media library. `onChange(mediaId, mediaObject)` fires on select.
 */
export const MediaPicker = ({ value, valueObject, onChange, label = "Afbeelding" }) => {
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState([]);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewObj, setPreviewObj] = useState(valueObject || null);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const { data } = await apiClient.get("/admin/media");
        setMedia(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    load();
  }, [open]);

  // Resolve preview when value changes
  useEffect(() => {
    if (valueObject) {
      setPreviewObj(valueObject);
      return;
    }
    if (!value) {
      setPreviewObj(null);
      return;
    }
    apiClient
      .get("/admin/media")
      .then(({ data }) => {
        const found = data.find((m) => m.id === value);
        setPreviewObj(found || null);
      })
      .catch(() => {});
  }, [value, valueObject]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Afbeelding geüpload");
      setMedia((m) => [data, ...m]);
      onChange(data.id, data);
      setPreviewObj(data);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onPick = (m) => {
    onChange(m.id, m);
    setPreviewObj(m);
    setOpen(false);
  };

  const onClear = () => {
    onChange(null, null);
    setPreviewObj(null);
  };

  const filtered = search
    ? media.filter((m) =>
        (m.original_filename + " " + (m.alt_text || "") + " " + (m.title || ""))
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : media;

  return (
    <div className="flex flex-col gap-2" data-testid="media-picker">
      <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
        {label}
      </span>

      {previewObj ? (
        <div className="relative bg-ink-950 border border-white/[0.06] aspect-[16/9] overflow-hidden group">
          <img
            src={mediaUrl(previewObj)}
            alt={previewObj.alt_text || ""}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-ink-950/0 group-hover:bg-ink-950/60 transition-colors flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="media-picker-replace"
              className="px-4 py-2 text-[10px] tracking-[0.3em] uppercase border border-white/30 text-white hover:border-champagne hover:text-champagne transition-colors"
            >
              Vervangen
            </button>
            <button
              type="button"
              onClick={onClear}
              data-testid="media-picker-clear"
              className="px-4 py-2 text-[10px] tracking-[0.3em] uppercase border border-white/30 text-white hover:border-red-500 hover:text-red-400 transition-colors"
            >
              Verwijderen
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="media-picker-open"
          className="bg-ink-950 border border-dashed border-white/10 hover:border-champagne/40 aspect-[16/9] flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-champagne transition-colors"
        >
          <ImageIcon size={22} />
          <span className="text-xs tracking-[0.2em] uppercase font-body">
            Afbeelding kiezen
          </span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="media-picker-dialog"
          className="bg-ink-900 border-white/[0.06] text-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">
              Mediabibliotheek
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col md:flex-row md:items-center gap-4 mt-2">
            <div className="relative flex-1">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op bestandsnaam of alt-tekst..."
                className="pl-9 h-10 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
              />
            </div>
            <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold cursor-pointer">
              <Upload size={13} />
              {uploading ? "Bezig..." : "Nieuw uploaden"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onUpload}
                disabled={uploading}
                className="hidden"
                data-testid="media-picker-upload"
              />
            </label>
          </div>

          <div className="flex-1 overflow-y-auto mt-4 -mx-6 px-6">
            {filtered.length === 0 ? (
              <div className="py-16 text-center text-sm text-zinc-500 font-body">
                Nog geen afbeeldingen geüpload.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pb-4">
                {filtered.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => onPick(m)}
                    data-testid={`media-picker-item-${m.id}`}
                    className="bg-ink-950 border border-white/[0.06] hover:border-champagne/40 aspect-[4/3] overflow-hidden transition-colors group"
                  >
                    <img
                      src={mediaUrl(m)}
                      alt={m.alt_text || m.original_filename}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaPicker;
